/**
 * Doc 06's four properties as doc 10's four markers — "same marker everywhere,
 * no exceptions".
 *
 * The placeholder art broke that rule without meaning to: a Shellback's armour
 * was three bands somebody drew on a crab, an Ironscale Ray's was a different
 * three bands somebody drew on a ray, Swift was a streak on exactly one fish,
 * and Regen had no picture at all. Four species wearing four dialects of the
 * same word is precisely the failure doc 10 legislates against, so the markers
 * are now overlays applied by the builder from the content flags — a species
 * cannot forget one, and no content edit can strand one.
 *
 * `regen` deviates from doc 10's literal wording, and does so to obey it: the
 * doc says "drifting green motes", written when D1's bioluminescence WAS green
 * (`#b7ff5e`). Taken literally at THE FLOOR the motes would be `#5ff2c8` — the
 * player's own accent colour, on an enemy. They take the depth's enemy glow,
 * which is what "green" meant.
 */

import { signatureRamp, type DepthPalette } from '../render/palette.ts';

import { faunaRamp, OUTLINE } from '../render/palette.ts';
import { PixelSurface, shade, type Color } from '../render/surface.ts';

import { step } from './organic.ts';
import type { CreatureAnchor } from './creature.ts';

/** Body faces a plate band is allowed to overwrite: flesh, never light or bone. */
function isFlesh(c: Color): boolean {
  const f = faunaRamp();
  return c === f.back || c === f.flank || c === f.belly;
}

/**
 * ARMORED — plate bands.
 *
 * Recolours flesh in place rather than adding geometry, so the silhouette is
 * bit-identical to the unarmoured animal: armour changes what a fish is made
 * of, not what shape it is, and `silhouetteUniqueness` must not be able to tell
 * two species apart by a property either of them could have.
 */
export function armored(s: PixelSurface, c: CreatureAnchor): void {
  const bone = faunaRamp().bone;
  const before = s.ops;
  for (let x = 0; x < s.width; x++) {
    if ((((x - c.ax) % 5) + 5) % 5 !== 0) continue;
    for (let y = 0; y < s.height; y++) {
      if (isFlesh(s.get(x, y))) s.px(x, y, bone, 'set');
    }
  }
  s.ops = before + 1; // one banding pass
}

/**
 * SWIFT — motion streaks.
 *
 * Three dashes trailing the tail, stepping with the phase so the creature
 * looks like it is outrunning its own wake. Drawn from the body's own western
 * edge so the streaks belong to the animal that is actually there.
 */
export function swift(s: PixelSurface, c: CreatureAnchor, phase: number): void {
  const f = faunaRamp();
  const b = s.inkBounds();
  if (!b) return;
  const lag = step(phase, 3);
  const rows = [c.ay - 2, c.ay, c.ay + 2];
  rows.forEach((y, k) => {
    const len = 3 - ((k + lag) % 3);
    const x1 = b.x0 - 1 - ((k + lag) % 2);
    s.hline(x1 - len, x1, y, k === 1 ? f.flank : f.fin, 'set');
  });
}

/**
 * REGEN — motes of its own light, drifting off its back.
 *
 * Four pixels on two phases. They rise, which is the read: something is
 * leaving the animal and the animal is fine. Dim (`halo`, not `on`) so a
 * healing fish never out-glows a riled one.
 */
export function regen(s: PixelSurface, c: CreatureAnchor, depth: DepthPalette, phase: number): void {
  const g = signatureRamp(depth.enemyGlow);
  const b = s.inkBounds();
  if (!b) return;
  const rise = step(phase, 4);
  const spots: Array<[number, number]> = [
    [-5, -1],
    [0, -3],
    [4, -1],
    [-2, -2],
  ];
  spots.forEach(([dx, dy], k) => {
    const y = b.y0 + dy - ((rise + k) % 3);
    s.px(c.ax + dx, y, (k + rise) % 2 === 0 ? g.halo : g.dim, 'set');
  });
}

/**
 * CLOAKED — doc 10's "40% alpha + shimmer", applied after the outline.
 *
 * It stays VISIBLE on purpose (doc 06): "there is something there and you
 * cannot shoot it" is the lesson, and a fish that vanished entirely would read
 * as a rendering bug instead of a mechanic. The dither parity walks with the
 * phase, which is the shimmer.
 *
 * The numbers below are floored on legibility rather than taken from doc 10
 * literally, and the browser is what caught it: 0.72 tint under a flat 0.4
 * alpha DOUBLE-DIPS, because the dither has already removed half the ink. On
 * THE GLOOM's water a cloaked Lanternjaw composited to 0.073 above the
 * background — 27% of its solid read, and on screen it was a smudge you could
 * lose. At 0.55 alpha and a 0.85 tint it composites to 0.124, about 47% of
 * solid, which is still unmistakably a ghost. `cloakedRead` in the harness now
 * measures exactly this so it cannot drift back.
 *
 * The outline stays fully opaque. It buys almost nothing on D2/D3 (a near-black
 * line on near-black water is invisible either way) but it keeps the shape
 * crisp on THE SHELF, where the water is light enough for the outline to work.
 */
const CLOAK_ALPHA = 0.55;
const CLOAK_TINT = 0.85;

export function cloak(src: PixelSurface, phase: number): PixelSurface {
  const parity = step(phase, 2);
  const alpha = Math.round(255 * CLOAK_ALPHA);
  const out = new PixelSurface(src.width, src.height);
  for (let y = 0; y < src.height; y++) {
    for (let x = 0; x < src.width; x++) {
      if (((x + y) & 1) !== parity) continue;
      const col = src.get(x, y);
      if (col === 0) continue;
      if (col === OUTLINE) out.px(x, y, OUTLINE, 'set');
      else out.px(x, y, (((shade(col, CLOAK_TINT) & 0xffffff00) >>> 0) | alpha) >>> 0, 'set');
    }
  }
  return out.resetOps();
}
